/**
 * Build a flat map of form field keys → human labels for the document filler AI.
 * Repeatable fields use `groupKey[].FIELD_ID`.
 * Conditional fields use `groupKey.options.<option>.FIELD_ID` (not flat root keys).
 *
 * @param {unknown} formSchema
 * @returns {Record<string, string>}
 */
export function buildDocumentFillerFieldLabels(formSchema) {
  const labels = {};
  if (!Array.isArray(formSchema)) return labels;

  for (const section of formSchema) {
    if (!section || typeof section !== "object") continue;

    const sectionLabel =
      typeof section.sectionLabel === "string" ? section.sectionLabel.trim() : "";

    if (section.repeatable && section.groupKey) {
      const groupKey = String(section.groupKey);
      if (sectionLabel) labels[groupKey] = sectionLabel;
      collectFields(section.fields, `${groupKey}[]`, sectionLabel, labels, {
        alsoFlat: false,
      });
      continue;
    }

    if (section.type === "conditional" && section.groupKey) {
      const groupKey = String(section.groupKey);
      if (sectionLabel) {
        labels[groupKey] = `${sectionLabel} (objeto { type, fields })`;
      }
      const options = section.options;
      if (options && typeof options === "object") {
        for (const [optionKey, fields] of Object.entries(options)) {
          const optionLabel = sectionLabel
            ? `${sectionLabel} › opção "${optionKey}"`
            : `opção "${optionKey}"`;
          collectFields(
            fields,
            `${groupKey}.options.${optionKey}`,
            optionLabel,
            labels,
            { alsoFlat: false }
          );
        }
      }
      continue;
    }

    collectFields(section.fields, null, sectionLabel, labels, { alsoFlat: true });
  }

  return labels;
}

function collectFields(fields, keyPrefix, sectionLabel, labels, { alsoFlat }) {
  if (!Array.isArray(fields)) return;

  for (const field of fields) {
    if (!field || typeof field !== "object" || !field.id) continue;

    const fieldId = String(field.id);
    const fieldLabel =
      typeof field.label === "string" && field.label.trim()
        ? field.label.trim()
        : fieldId;
    const composed = sectionLabel
      ? `${sectionLabel} › ${fieldLabel}`
      : fieldLabel;
    const mapKey = keyPrefix ? `${keyPrefix}.${fieldId}` : fieldId;
    labels[mapKey] = composed;

    if (alsoFlat && !keyPrefix) {
      labels[fieldId] = composed;
    }
  }
}

export default buildDocumentFillerFieldLabels;
